// Plan holdings layer family — holding-pattern racetracks for procedure legs.
//
// Dedicated source (`plan-holdings`) so holding patterns can be rendered
// with a different layer style from the main plan line. Drives two layers:
//   - plan-holding-glow: faint magenta glow halo
//   - plan-holding-line: dashed magenta line
//
// NOTE: id suffix '-mount' to avoid colliding with any future
// registerStyleRebuild that might own apply for this family. The
// data is pushed by pushPlan(map) called from MapView's styledata
// handler (which also fills the plan-holdings source). See
// src/map/layers/terrain.ts for the canonical pattern.
// Extracted from addOverlays.ts as HI-1 batch 8 — final batch (2026-05-24).
// Pure mechanical move — source spec, paint expressions, filter expressions,
// layer ids, and layer order all byte-identical to the pre-extraction form.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';

/**
 * Mount the plan-holdings source and layers.
 * Idempotent on the source.
 */
export function addPlanHoldingsLayers(map: MlMap): void {
  if (!map.getSource('plan-holdings')) {
    map.addSource('plan-holdings', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'plan-holding-glow',
      type: 'line',
      source: 'plan-holdings',
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: {
        'line-color': '#e040fb',
        'line-width': 6,
        'line-opacity': 0.2,
        'line-blur': 2,
      },
    });
    map.addLayer({
      id: 'plan-holding-line',
      type: 'line',
      source: 'plan-holdings',
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: {
        'line-color': '#e040fb',
        'line-width': 2,
        'line-dasharray': [2, 1],
      },
    });
  }
}

// NOTE: id suffix '-mount' to avoid colliding with any future
// registerStyleRebuild that might own apply for this family. The
// data is pushed by pushPlan(map) called from MapView's styledata
// handler. See src/map/layers/terrain.ts for the canonical pattern.
registerLayerFamily({
  id: 'plan-holdings-mount',
  label: 'Flight plan holdings',
  sourceId: 'plan-holdings',
  layerIds: ['plan-holding-glow', 'plan-holding-line'],
  mount: (map) => addPlanHoldingsLayers(map),
  // No applyState — pushPlan() owns data re-push for this source too.
});
